import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { useAuth } from './authContext';
import type { KnowledgeNode, Edge, EdgeLabel, SchemaRule } from '../../types/knowledge';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';

interface KnowledgeContextType {
  nodes: KnowledgeNode[];
  edges: Edge[];
  edgeLabels: EdgeLabel[];
  schemaRules: SchemaRule[];
  selfNode: KnowledgeNode | null;
  loading: boolean;
  /**
   * True once the first graph fetch has completed (success or error).
   * `loading` alone is insufficient to gate empty states: it initializes to
   * `false` and only flips `true` inside the effect-driven `refreshGraph`, so
   * the first render tick (and the pre-auth window) would otherwise flash an
   * empty state. Gate "no data" UI on `initialFetchDone`, not `loading`.
   */
  initialFetchDone: boolean;
  nodeCount: number;
  hasMoreGraph: boolean;
  loadingMoreGraph: boolean;
  error: string | null;

  // CRUD operations for nodes
  addNode: (node: { id?: string; title: string | null; is_self_node: boolean }) => Promise<KnowledgeNode | null>;
  updateNode: (id: string, updates: Partial<KnowledgeNode>) => Promise<{ error: any }>;
  deleteNode: (id: string) => Promise<{ error: any }>;

  // Edge operations — node edges (from_id -> to_id with edge_label_id)
  addEdge: (fromId: string, toId: string, edgeLabelId: string) => Promise<Edge | null>;
  // Scalar edge (from_id, value — no to_id)
  addScalarEdge: (fromId: string, value: unknown, edgeLabelId: string) => Promise<Edge | null>;
  updateEdge: (id: string, updates: Partial<{ value: unknown; to_id: string | null; edge_label_id: string }>) => Promise<{ error: any }>;
  deleteEdge: (id: string) => Promise<{ error: any }>;

  // Edge label CRUD
  createEdgeLabel: (data: Omit<EdgeLabel, 'id' | 'created_at' | 'user_id' | 'is_system_managed'>) => Promise<EdgeLabel | null>;
  updateEdgeLabel: (id: string, data: Partial<EdgeLabel>) => Promise<{ error: any }>;
  deleteEdgeLabel: (id: string) => Promise<{ error: any }>;

  // Schema rule CRUD
  createSchemaRule: (data: Pick<SchemaRule, 'trigger_edge_label_id' | 'trigger_node_id' | 'implied_edges'>) => Promise<SchemaRule | null>;
  updateSchemaRule: (id: string, data: Partial<Pick<SchemaRule, 'trigger_edge_label_id' | 'trigger_node_id' | 'implied_edges'>>) => Promise<{ error: any }>;
  deleteSchemaRule: (id: string) => Promise<{ error: any }>;

  // Query helpers
  searchNodes: (query: string) => KnowledgeNode[];
  getRelatedNodes: (nodeId: string) => KnowledgeNode[];
  getNextReviewNode: () => KnowledgeNode | null;
  getEdgesFrom: (nodeId: string) => Edge[];
  getEdgesTo: (nodeId: string) => Edge[];
  getInstanceOf: (nodeId: string) => KnowledgeNode[];

  // Refresh
  refreshGraph: () => Promise<void>;
  refreshSchemaRules: () => Promise<void>;
  loadMoreGraph: () => Promise<void>;
  ensureNodesLoaded: (ids: string[]) => Promise<void>;
  ensureNodeEdgesLoaded: (nodeId: string) => Promise<void>;
  ensureNodeSchemaContext: (nodeId: string) => Promise<void>;
  ensureEdgeLoaded: (edgeId: string) => Promise<void>;
}

const KnowledgeContext = createContext<KnowledgeContextType | undefined>(undefined);
const GRAPH_PAGE_SIZE = 96;

function mergeById<T extends { id: string }>(existing: T[], incoming: T[]): T[] {
  const merged = new Map(existing.map((row) => [row.id, row]));
  for (const row of incoming) merged.set(row.id, row);
  return [...merged.values()];
}

// PostgREST caps responses at db-max-rows (1000 in this project), so any table
// large enough to cross that threshold must be paginated. Returns every row.
async function fetchAllRows<T>(buildQuery: () => any): Promise<T[]> {
  const PAGE = 1000;
  const out: T[] = [];
  for (let offset = 0; ; offset += PAGE) {
    const { data, error } = await buildQuery().range(offset, offset + PAGE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    out.push(...(data as T[]));
    if (data.length < PAGE) break;
  }
  return out;
}

// Flatten a scalar edge value into plain searchable text. Values arrive as raw
// JS, a JSON-encoded string, a typed collection, or an object (date_range),
// so recurse until every piece becomes a searchable string.
function scalarToText(value: unknown): string {
  if (value == null) return '';
  let v: unknown = value;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch { return value as string; }
  }
  if (v == null) return '';
  if (Array.isArray(v)) return v.map(scalarToText).join(' ');
  if (typeof v === 'object') return Object.values(v as Record<string, unknown>).map(scalarToText).join(' ');
  return String(v);
}

export function KnowledgeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [nodes, setNodes] = useState<KnowledgeNode[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const definitionRevision = useRef(0);
  const [edgeLabels, setEdgeLabels] = useState<EdgeLabel[]>([]);
  const [schemaRules, setSchemaRules] = useState<SchemaRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [nodeCount, setNodeCount] = useState(0);
  const [hasMoreNodes, setHasMoreNodes] = useState(false);
  const [hasMoreEdges, setHasMoreEdges] = useState(false);
  const [loadingMoreGraph, setLoadingMoreGraph] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nodeOffsetRef = useRef(0);
  const edgeOffsetRef = useRef(0);
  const loadingMoreRef = useRef(false);

  const refreshSchemaRules = useCallback(async () => {
    if (!user) return;

    const revision = definitionRevision.current;
    const { data } = await supabase
      .from('schema_rules')
      .select('*')
      .or(`user_id.is.null,user_id.eq.${user.id}`)
      .order('created_at');

    if (data && revision === definitionRevision.current) setSchemaRules(data as SchemaRule[]);
  }, [user]);

  const ensureNodesLoaded = useCallback(async (ids: string[]) => {
    if (!user || ids.length === 0) return;
    const uniqueIds = [...new Set(ids)];
    const { data, error: fetchError } = await supabase
      .from('knowledge_nodes')
      .select('*')
      .eq('user_id', user.id)
      .in('id', uniqueIds);
    if (fetchError) throw fetchError;
    if (data?.length) setNodes((current) => mergeById(current, data as KnowledgeNode[]));
  }, [user]);

  const hydrateEdgeNodes = useCallback(async (rows: Edge[]) => {
    const ids = rows.flatMap((edge) => edge.to_id ? [edge.from_id, edge.to_id] : [edge.from_id]);
    await ensureNodesLoaded(ids);
  }, [ensureNodesLoaded]);

  // Core fetch. `silent` skips the loading/error flags so realtime-driven
  // refreshes don't flash the UI or surface transient errors mid-edit.
  const loadGraph = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!user) return;
    const revision = definitionRevision.current;

    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      // Load one stable page of the high-cardinality graph tables. Collection
      // views request subsequent pages before their scroll sentinel is reached.
      const [nodesResult, edgesResult, edgeLabelsData, selfNodeResult] = await Promise.all([
        supabase.from('knowledge_nodes').select('*', { count: 'exact' }).eq('user_id', user.id)
          .order('created_at', { ascending: false }).order('id', { ascending: false }).range(0, GRAPH_PAGE_SIZE - 1),
        supabase.from('edges').select('*', { count: 'exact' }).eq('user_id', user.id)
          .order('created_at', { ascending: false }).order('id', { ascending: false }).range(0, GRAPH_PAGE_SIZE - 1),
        fetchAllRows<EdgeLabel>(() =>
          supabase.from('edge_labels').select('*').or(`user_id.is.null,user_id.eq.${user.id}`)
        ),
        supabase.from('knowledge_nodes').select('*').eq('user_id', user.id).eq('is_self_node', true).maybeSingle(),
      ]);

      if (nodesResult.error) throw nodesResult.error;
      if (edgesResult.error) throw edgesResult.error;
      if (selfNodeResult.error) throw selfNodeResult.error;
      const nodesData = (nodesResult.data ?? []) as KnowledgeNode[];
      const edgesData = (edgesResult.data ?? []) as Edge[];
      const initialNodes = selfNodeResult.data
        ? mergeById(nodesData, [selfNodeResult.data as KnowledgeNode])
        : nodesData;

      setNodes((current) => silent ? mergeById(current, initialNodes) : initialNodes);
      setEdges((current) => silent ? mergeById(current, edgesData) : edgesData);
      if (revision === definitionRevision.current) setEdgeLabels(edgeLabelsData);
      setNodeCount(nodesResult.count ?? nodesData.length);
      nodeOffsetRef.current = silent ? Math.max(nodeOffsetRef.current, nodesData.length) : nodesData.length;
      edgeOffsetRef.current = silent ? Math.max(edgeOffsetRef.current, edgesData.length) : edgesData.length;
      setHasMoreNodes(nodesResult.count != null ? nodeOffsetRef.current < nodesResult.count : nodesData.length === GRAPH_PAGE_SIZE);
      setHasMoreEdges(edgesResult.count != null ? edgeOffsetRef.current < edgesResult.count : edgesData.length === GRAPH_PAGE_SIZE);
      await hydrateEdgeNodes(edgesData);

      // Also refresh schema rules (low-cardinality, cheap)
      await refreshSchemaRules();
    } catch (err) {
      if (!silent) setError(err instanceof Error ? err.message : 'Failed to load knowledge graph');
    } finally {
      // Silent (realtime) refetches must not toggle the loading spinner, but the
      // first fetch must always flip initialFetchDone so empty states can gate on
      // it (idempotent on later refetches).
      if (!silent) setLoading(false);
      setInitialFetchDone(true);
    }
  }, [user, refreshSchemaRules, hydrateEdgeNodes]);

  const refreshGraph = useCallback(() => loadGraph(), [loadGraph]);

  const loadMoreGraph = useCallback(async () => {
    if (!user || loadingMoreRef.current || (!hasMoreNodes && !hasMoreEdges)) return;
    loadingMoreRef.current = true;
    setLoadingMoreGraph(true);
    try {
      const [nodesResult, edgesResult] = await Promise.all([
        hasMoreNodes
          ? supabase.from('knowledge_nodes').select('*', { count: 'exact' }).eq('user_id', user.id)
              .order('created_at', { ascending: false }).order('id', { ascending: false })
              .range(nodeOffsetRef.current, nodeOffsetRef.current + GRAPH_PAGE_SIZE - 1)
          : Promise.resolve({ data: [] as KnowledgeNode[], error: null, count: nodeOffsetRef.current }),
        hasMoreEdges
          ? supabase.from('edges').select('*', { count: 'exact' }).eq('user_id', user.id)
              .order('created_at', { ascending: false }).order('id', { ascending: false })
              .range(edgeOffsetRef.current, edgeOffsetRef.current + GRAPH_PAGE_SIZE - 1)
          : Promise.resolve({ data: [] as Edge[], error: null, count: edgeOffsetRef.current }),
      ]);
      if (nodesResult.error) throw nodesResult.error;
      if (edgesResult.error) throw edgesResult.error;
      const nextNodes = (nodesResult.data ?? []) as KnowledgeNode[];
      const nextEdges = (edgesResult.data ?? []) as Edge[];
      nodeOffsetRef.current += nextNodes.length;
      edgeOffsetRef.current += nextEdges.length;
      setNodes((current) => mergeById(current, nextNodes));
      setEdges((current) => mergeById(current, nextEdges));
      setNodeCount(nodesResult.count ?? nodeCount);
      setHasMoreNodes(nodesResult.count != null ? nodeOffsetRef.current < nodesResult.count : nextNodes.length === GRAPH_PAGE_SIZE);
      setHasMoreEdges(edgesResult.count != null ? edgeOffsetRef.current < edgesResult.count : nextEdges.length === GRAPH_PAGE_SIZE);
      await hydrateEdgeNodes(nextEdges);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more graph data');
    } finally {
      loadingMoreRef.current = false;
      setLoadingMoreGraph(false);
    }
  }, [user, hasMoreNodes, hasMoreEdges, hydrateEdgeNodes, nodeCount]);

  const ensureNodeEdgesLoaded = useCallback(async (nodeId: string) => {
    if (!user) return;
    const { data, error: fetchError } = await supabase
      .from('edges')
      .select('*')
      .eq('user_id', user.id)
      .or(`from_id.eq.${nodeId},to_id.eq.${nodeId}`);
    if (fetchError) throw fetchError;
    const rows = (data ?? []) as Edge[];
    setEdges((current) => mergeById(current, rows));
    await hydrateEdgeNodes(rows);
  }, [user, hydrateEdgeNodes]);

  const ensureEdgeLoaded = useCallback(async (edgeId: string) => {
    if (!user) return;
    const { data, error: fetchError } = await supabase
      .from('edges').select('*').eq('user_id', user.id).eq('id', edgeId).maybeSingle();
    if (fetchError) throw fetchError;
    if (!data) return;
    const row = data as Edge;
    setEdges((current) => mergeById(current, [row]));
    await hydrateEdgeNodes([row]);
  }, [user, hydrateEdgeNodes]);

  const ensureNodeSchemaContext = useCallback(async (nodeId: string) => {
    if (!user) return;
    await ensureNodeEdgesLoaded(nodeId);
    const isInstanceOfId = edgeLabels.find((label) => label.label === 'is instance of')?.id;
    if (!isInstanceOfId) return;
    const visited = new Set<string>();
    let frontier = [nodeId];
    while (frontier.length > 0) {
      const current = frontier.filter((id) => !visited.has(id));
      if (current.length === 0) break;
      current.forEach((id) => visited.add(id));
      const { data, error: fetchError } = await supabase
        .from('edges')
        .select('*')
        .eq('user_id', user.id)
        .eq('edge_label_id', isInstanceOfId)
        .in('from_id', current);
      if (fetchError) throw fetchError;
      const rows = (data ?? []) as Edge[];
      setEdges((existing) => mergeById(existing, rows));
      await hydrateEdgeNodes(rows);
      frontier = rows.flatMap((edge) => edge.to_id ? [edge.to_id] : []);
    }
  }, [user, edgeLabels, ensureNodeEdgesLoaded, hydrateEdgeNodes]);

  // Load graph when user changes
  useEffect(() => {
    if (user) {
      loadGraph();
    } else {
      setNodes([]);
      setEdges([]);
      setEdgeLabels([]);
      setSchemaRules([]);
      setNodeCount(0);
      setHasMoreNodes(false);
      setHasMoreEdges(false);
      nodeOffsetRef.current = 0;
      edgeOffsetRef.current = 0;
    }
  }, [user, loadGraph]);

  useRealtimeRefresh({
    channelName: 'knowledge-graph-live-data',
    enabled: !!user,
    tables: [
      { table: 'knowledge_nodes', filter: user ? `user_id=eq.${user.id}` : undefined },
      { table: 'edges', filter: user ? `user_id=eq.${user.id}` : undefined },
      { table: 'edge_labels' },
      { table: 'schema_rules' },
    ],
    refresh: () => loadGraph({ silent: true }),
    debounceMs: 300,
  });

  const addNode = async (node: { id?: string; title: string | null; is_self_node: boolean }) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('knowledge_nodes')
        .insert({
          ...node,
          user_id: user.id,
        })
        .select('id, user_id, title, is_self_node, native_kind, system_key, management_mode, is_system_managed, created_at, updated_at')
        .single();

      if (error) throw error;

      const newNode = data as KnowledgeNode;
      setNodes(prev => [newNode, ...prev]);
      setNodeCount(count => count + 1);
      return newNode;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add node');
      return null;
    }
  };

  const updateNode = async (id: string, updates: Partial<KnowledgeNode>) => {
    if (!user) return { error: 'Not authenticated' };

    try {
      const { user_id: _owner, is_system_managed: _managed, management_mode: _mode, ...editable } = updates;
      const { error } = await supabase
        .from('knowledge_nodes')
        .update(editable)
        .eq('id', id)
        .eq('user_id', user.id)
        .eq('is_system_managed', false);

      if (error) throw error;

      setNodes(prev =>
        prev.map(node => node.id === id ? { ...node, ...editable } : node)
      );
      return { error: null };
    } catch (err) {
      return { error: err };
    }
  };

  const deleteNode = async (id: string) => {
    if (!user) return { error: 'Not authenticated' };

    try {
      const { error } = await supabase
        .from('knowledge_nodes')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id)
        .eq('is_system_managed', false);

      if (error) throw error;

      setNodes(prev => prev.filter(node => node.id !== id));
      setNodeCount(count => Math.max(0, count - 1));
      // Also remove related edges
      setEdges(prev => prev.filter(edge => edge.from_id !== id && edge.to_id !== id));
      return { error: null };
    } catch (err) {
      return { error: err };
    }
  };

  const addEdge = async (fromId: string, toId: string, edgeLabelId: string) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('edges')
        .insert({
          user_id: user.id,
          from_id: fromId,
          to_id: toId,
          edge_label_id: edgeLabelId,
        })
        .select('id, user_id, from_id, to_id, edge_label_id, value, management_mode, is_system_managed, created_at')
        .single();

      if (error) throw error;

      const newEdge = data as Edge;
      setEdges(prev => [...prev, newEdge]);
      return newEdge;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add edge');
      return null;
    }
  };

  const addScalarEdge = async (fromId: string, value: unknown, edgeLabelId: string) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('edges')
        .insert({
          user_id: user.id,
          from_id: fromId,
          to_id: null,
          value,
          edge_label_id: edgeLabelId,
        })
        .select('id, user_id, from_id, to_id, edge_label_id, value, management_mode, is_system_managed, created_at')
        .single();

      if (error) throw error;

      const newEdge = data as Edge;
      setEdges(prev => [...prev, newEdge]);
      return newEdge;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add scalar edge');
      return null;
    }
  };

  const updateEdge = async (id: string, updates: Partial<{ value: unknown; to_id: string | null; edge_label_id: string }>) => {
    if (!user) return { error: 'Not authenticated' };
    try {
      const { error } = await supabase.from('edges').update(updates).eq('id', id).eq('user_id', user.id).eq('is_system_managed', false);
      if (error) throw error;
      setEdges(prev => prev.map(e => e.id === id ? { ...e, ...updates } as Edge : e));
      return { error: null };
    } catch (err) { return { error: err }; }
  };

  const deleteEdge = async (id: string) => {
    if (!user) return { error: 'Not authenticated' };

    try {
      const { error } = await supabase
        .from('edges')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id)
        .eq('is_system_managed', false);

      if (error) throw error;

      setEdges(prev => prev.filter(edge => edge.id !== id));
      return { error: null };
    } catch (err) {
      return { error: err };
    }
  };

  const createEdgeLabel = async (data: Omit<EdgeLabel, 'id' | 'created_at' | 'user_id' | 'is_system_managed'>) => {
    if (!user) return null;
    try {
      // Stamp the authenticated user — the user-scoped write policy
      // (migration 182) requires user_id = auth.uid() on insert.
      const { data: row, error } = await supabase
        .from('edge_labels')
        .insert({ ...data, user_id: user.id, is_system_managed: false })
        .select('*')
        .single();
      if (error) throw error;
      definitionRevision.current += 1;
      const newLabel = row as EdgeLabel;
      setEdgeLabels(prev => [...prev, newLabel]);
      return newLabel;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create edge label');
      return null;
    }
  };

  const updateEdgeLabel = async (id: string, data: Partial<EdgeLabel>) => {
    try {
      const { user_id: _owner, is_system_managed: _managed, management_mode: _mode, ...editable } = data;
      const { error } = await supabase.from('edge_labels').update(editable).eq('id', id).eq('is_system_managed', false);
      if (error) throw error;
      definitionRevision.current += 1;
      setEdgeLabels(prev => prev.map(el => el.id === id ? { ...el, ...editable } as EdgeLabel : el));
      return { error: null };
    } catch (err) { return { error: err }; }
  };

  const deleteEdgeLabel = async (id: string) => {
    try {
      const { error } = await supabase.from('edge_labels').delete().eq('id', id).eq('is_system_managed', false);
      if (error) throw error;
      definitionRevision.current += 1;
      setEdgeLabels(prev => prev.filter(el => el.id !== id));
      return { error: null };
    } catch (err) { return { error: err }; }
  };

  const createSchemaRule = async (data: Pick<SchemaRule, 'trigger_edge_label_id' | 'trigger_node_id' | 'implied_edges'>) => {
    if (!user) return null;
    try {
      const { data: row, error } = await supabase
        .from('schema_rules')
        .insert({ ...data, user_id: user.id, is_system_managed: false })
        .select('*')
        .single();
      if (error) throw error;
      definitionRevision.current += 1;
      const newRule = row as SchemaRule;
      setSchemaRules(prev => [...prev, newRule]);
      return newRule;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create schema rule');
      return null;
    }
  };

  const updateSchemaRule = async (id: string, data: Partial<Pick<SchemaRule, 'trigger_edge_label_id' | 'trigger_node_id' | 'implied_edges'>>) => {
    if (!user) return { error: 'Not authenticated' };
    try {
      const { error } = await supabase.from('schema_rules').update(data).eq('id', id).eq('user_id', user.id).eq('is_system_managed', false);
      if (error) throw error;
      definitionRevision.current += 1;
      setSchemaRules(prev => prev.map(r => r.id === id ? { ...r, ...data } as SchemaRule : r));
      return { error: null };
    } catch (err) { return { error: err }; }
  };

  const deleteSchemaRule = async (id: string) => {
    if (!user) return { error: 'Not authenticated' };
    try {
      const { error } = await supabase.from('schema_rules').delete().eq('id', id).eq('user_id', user.id).eq('is_system_managed', false);
      if (error) throw error;
      definitionRevision.current += 1;
      setSchemaRules(prev => prev.filter(r => r.id !== id));
      return { error: null };
    } catch (err) { return { error: err }; }
  };

  // Lowercased searchable text per node: its title plus every scalar value
  // attached to it. Lets search match on a node's content (definitions, dates,
  // tags…), not just its title — so "iambic" finds "iambic pentameter" and any
  // node whose definition mentions it.
  const nodeSearchText = useMemo(() => {
    const map = new Map<string, string>();
    for (const n of nodes) map.set(n.id, (n.title || '').toLowerCase());
    for (const e of edges) {
      if (e.to_id || e.value == null) continue; // only scalar edges carry text
      const prev = map.get(e.from_id);
      if (prev === undefined) continue; // edge from a node we don't hold
      map.set(e.from_id, `${prev} ${scalarToText(e.value).toLowerCase()}`);
    }
    return map;
  }, [nodes, edges]);

  const searchNodes = useCallback((query: string): KnowledgeNode[] => {
    const q = query.trim().toLowerCase();
    if (!q) return nodes;
    return nodes.filter(node => (nodeSearchText.get(node.id) || '').includes(q));
  }, [nodeSearchText, nodes]);

  const getRelatedNodes = (nodeId: string): KnowledgeNode[] => {
    const relatedIds = new Set<string>();

    edges.forEach(edge => {
      if (edge.from_id === nodeId && edge.to_id) relatedIds.add(edge.to_id);
      if (edge.to_id === nodeId) relatedIds.add(edge.from_id);
    });

    return nodes.filter(node => relatedIds.has(node.id));
  };

  const getNextReviewNode = (): KnowledgeNode | null => {
    // Only review nodes that are "is instance of → committed to memory"
    const isInstanceOfLabel = edgeLabels.find(el => el.label === 'is instance of');
    const committedToMemoryNode = nodes.find(n => n.title === 'committed to memory');
    if (!isInstanceOfLabel || !committedToMemoryNode) return null;

    const committedNodeIds = new Set(
      edges
        .filter(e => e.edge_label_id === isInstanceOfLabel.id && e.to_id === committedToMemoryNode.id)
        .map(e => e.from_id)
    );

    if (committedNodeIds.size === 0) return null;

    // Find "next review" scalar edges for committed to memory nodes
    const now = new Date().toISOString();
    const nextReviewLabel = edgeLabels.find(el => el.label === 'next review');
    const nextReviewLabelId = nextReviewLabel?.id;
    const reviewEdges = edges.filter(e => nextReviewLabelId && e.edge_label_id === nextReviewLabelId && !e.to_id && committedNodeIds.has(e.from_id));

    const dueNodeIds = new Set<string>();
    const reviewDates = new Map<string, string>();
    for (const e of reviewEdges) {
      const val = e.value as string;
      if (!val || val <= now) {
        dueNodeIds.add(e.from_id);
        reviewDates.set(e.from_id, val || '');
      }
    }

    // Also include committed nodes that have NO next_review edge (never reviewed)
    const nodesWithReview = new Set(reviewEdges.map(e => e.from_id));
    for (const nodeId of committedNodeIds) {
      if (!nodesWithReview.has(nodeId)) {
        dueNodeIds.add(nodeId);
      }
    }

    const dueNodes = nodes
      .filter(n => dueNodeIds.has(n.id))
      .sort((a, b) => {
        const aDate = reviewDates.get(a.id) || '';
        const bDate = reviewDates.get(b.id) || '';
        if (!aDate) return -1;
        if (!bDate) return 1;
        return aDate.localeCompare(bDate);
      });

    return dueNodes[0] || null;
  };

  const selfNode = nodes.find(n => n.is_self_node) || null;

  const getEdgesFrom = (nodeId: string): Edge[] => {
    return edges.filter(e => e.from_id === nodeId);
  };

  const getEdgesTo = (nodeId: string): Edge[] => {
    return edges.filter(e => e.to_id === nodeId);
  };

  const getInstanceOf = (nodeId: string): KnowledgeNode[] => {
    // Find "is instance of" edges from this node, return target nodes
    const isInstanceOfLabel = edgeLabels.find(el => el.label === 'is instance of');
    const isInstanceOfLabelId = isInstanceOfLabel?.id;
    const targetIds = edges
      .filter(e => e.from_id === nodeId && isInstanceOfLabelId && e.edge_label_id === isInstanceOfLabelId && e.to_id)
      .map(e => e.to_id!);
    return nodes.filter(n => targetIds.includes(n.id));
  };

  const value = useMemo(() => ({
    nodes,
    edges,
    edgeLabels,
    schemaRules,
    selfNode,
    loading,
    initialFetchDone,
    nodeCount,
    hasMoreGraph: hasMoreNodes || hasMoreEdges,
    loadingMoreGraph,
    error,
    addNode,
    updateNode,
    deleteNode,
    addEdge,
    addScalarEdge,
    updateEdge,
    deleteEdge,
    createEdgeLabel,
    updateEdgeLabel,
    deleteEdgeLabel,
    createSchemaRule,
    updateSchemaRule,
    deleteSchemaRule,
    searchNodes,
    getRelatedNodes,
    getNextReviewNode,
    getEdgesFrom,
    getEdgesTo,
    getInstanceOf,
    refreshGraph,
    refreshSchemaRules,
    loadMoreGraph,
    ensureNodesLoaded,
    ensureNodeEdgesLoaded,
    ensureNodeSchemaContext,
    ensureEdgeLoaded,
  }), [
    nodes, edges, edgeLabels, schemaRules, selfNode, loading, initialFetchDone, nodeCount, hasMoreNodes, hasMoreEdges, loadingMoreGraph, error,
    addNode, updateNode, deleteNode,
    addEdge, addScalarEdge, updateEdge, deleteEdge,
    createEdgeLabel, updateEdgeLabel, deleteEdgeLabel,
    createSchemaRule, updateSchemaRule, deleteSchemaRule,
    searchNodes, getRelatedNodes, getNextReviewNode,
    getEdgesFrom, getEdgesTo, getInstanceOf,
    refreshGraph, refreshSchemaRules, loadMoreGraph, ensureNodesLoaded, ensureNodeEdgesLoaded, ensureNodeSchemaContext, ensureEdgeLoaded,
  ]);

  return <KnowledgeContext.Provider value={value}>{children}</KnowledgeContext.Provider>;
}

export function useKnowledge() {
  const context = useContext(KnowledgeContext);
  if (context === undefined) {
    throw new Error('useKnowledge must be used within a KnowledgeProvider');
  }
  return context;
}
